import React from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import { TagTypesQuery, TagsQuery } from "arena/queries";
import ArenaContents from "arena/components/ArenaContents";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import HomepageTitle from "arena/components/HomepageTitle";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import indexById from "core/utils/indexById";
import TagsAutocomplete from "arena/components/TagsAutocomplete";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SkuflixSearchIcon from "core/icons/skuflix/SkuflixSearchIcon";
import SkuflixCrossIcon from "core/icons/skuflix/SkuflixCrossIcon";

const NULL = "NULL";
const LIMIT = 12;

const uploadDateOptions = [
  { label: "arenaArchive.today", value: "TODAY" },
  { label: "arenaArchive.thisWeek", value: "THIS_WEEK" },
  { label: "arenaArchive.thisMonth", value: "THIS_MONTH" },
  { label: "arenaArchive.thisYear", value: "THIS_YEAR" },
];

const durationOptions = [
  { label: "arenaArchive.lessThan4Min", value: "SHORT" },
  { label: "arenaArchive.between4And20Min", value: "MEDIUM" },
  { label: "arenaArchive.moreThan20Min", value: "LONG" },
];

export default function ArenaArchive({
  title,
  filterTagTypes = [],
  tagTypeId,
  tagsSort,
  arena,
  allVideosText,
  rightholderIds,
  filterTagsBySelectedTags,
  itemsPerRow,
  tagIds,
  advancedFilters,
  previewMode,
  search,
  sort = "LATEST",
}) {
  const { t } = useTranslation("arena");
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") || NULL;
  const queryTagIds = searchParams.getAll("tagId") || [];
  const uploadDate = searchParams.get("uploadDate") || null;
  const duration = searchParams.get("duration") || null;
  const searchQuery = searchParams.get("search") || "";

  const tagTypeIds = filterTagTypes
    .map((item) => item.tagTypeId)
    .concat(tagTypeId)
    .filter(Boolean);

  const { data } = useQuery(TagTypesQuery, {
    skip: tagTypeIds.length === 0,
    variables: {
      ids: tagTypeIds,
    },
  });

  const tagTypesById = indexById(data?.tagTypes);

  const quickLinkTagType = tagTypesById[tagTypeId];

  const tagTypesForFilter = filterTagTypes
    .map((item) => ({
      tagType: tagTypesById[item.tagTypeId],
      sort: item.sort,
    }))
    .filter((item) => item.tagType);

  const { data: quickLinkTagsData } = useQuery(TagsQuery, {
    skip: !quickLinkTagType,
    variables: {
      tagTypeId: quickLinkTagType?.id,
      sort: tagsSort,
    },
  });

  const quickLinkTags = quickLinkTagsData?.tags || [];

  const onChangeAutocomplete = (ids, newValue) => {
    const newTagIds = queryTagIds
      .filter((id) => !ids.includes(id))
      .concat(newValue?.value)
      .filter(Boolean);

    searchParams.delete("tagId");
    newTagIds.forEach((tagId) => searchParams.append("tagId", tagId));
    setSearchParams(searchParams);
  };

  const resultTagIds = Array.from(queryTagIds);

  if (tagIds?.length) {
    resultTagIds.push(...tagIds);
  }

  if (tab !== NULL) {
    resultTagIds.push(tab);
  }

  const onChangeTab = (event, newTab) => {
    if (newTab === NULL) {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", newTab);
    }
    setSearchParams(searchParams);
  };

  const onChangeUploadDate = (event, newValue) => {
    const value = newValue?.value || "";
    if (value) {
      searchParams.set("uploadDate", value);
    } else {
      searchParams.delete("uploadDate");
    }
    setSearchParams(searchParams);
  };

  const onChangeDuration = (event, newValue) => {
    const value = newValue?.value || "";
    if (value) {
      searchParams.set("duration", value);
    } else {
      searchParams.delete("duration");
    }
    setSearchParams(searchParams);
  };

  const onSearchChange = (event) => {
    const value = event.target.value;
    if (value) {
      searchParams.set("search", value);
    } else {
      searchParams.delete("search");
    }
    setSearchParams(searchParams);
  };

  const onClearSearch = () => {
    searchParams.delete("search");
    setSearchParams(searchParams);
  };

  const displayTabs =
    quickLinkTags.length > 0 ||
    tagTypesForFilter.length > 0 ||
    advancedFilters ||
    search;

  return (
    <>
      {title && <HomepageTitle title={title} />}

      <Box sx={!title ? { mt: 2 } : {}}>
        {displayTabs && (
          <Grid container spacing={2}>
            {quickLinkTags.length > 0 && (
              <Grid item xs={12}>
                <Tabs
                  value={tab}
                  onChange={onChangeTab}
                  variant="scrollable"
                  scrollButtons="auto"
                  indicatorColor="primary"
                  textColor="inherit"
                  aria-label="scrollable auto tabs example"
                >
                  <Tab
                    value={NULL}
                    label={allVideosText || t("allVideos")}
                    sx={{ fontSize: "1.1rem" }}
                  />
                  {quickLinkTags.map((tag) => (
                    <Tab
                      value={tag.id}
                      label={tag.title}
                      key={tag.id}
                      sx={{ fontSize: "1.1rem" }}
                    />
                  ))}
                </Tabs>
              </Grid>
            )}
            {(tagTypesForFilter.length > 0 || advancedFilters || search) && (
              <Grid item xs={12}>
                <Box sx={{ pl: { xs: 2, sm: 0 }, pr: { xs: 2, sm: 0 } }}>
                  <Grid container spacing={2}>
                    {search && (
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <TextField
                          fullWidth
                          value={searchQuery}
                          onChange={onSearchChange}
                          placeholder={t("arenaArchive.searchPlaceholder")}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={onClearSearch}
                                  size="small"
                                >
                                  {searchQuery ? (
                                    <SkuflixCrossIcon />
                                  ) : (
                                    <SkuflixSearchIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}
                    {tagTypesForFilter?.map(({ tagType, sort }) => {
                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          key={tagType.id}
                        >
                          <TagsAutocomplete
                            onChangeAutocomplete={onChangeAutocomplete}
                            tagIds={resultTagIds}
                            tagType={tagType}
                            sort={sort}
                            filterTagsBySelectedTags={filterTagsBySelectedTags}
                          />
                        </Grid>
                      );
                    })}
                    {advancedFilters && (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <Autocomplete
                            options={uploadDateOptions}
                            value={
                              uploadDateOptions.find(
                                (opt) => opt.value === uploadDate
                              ) || null
                            }
                            onChange={onChangeUploadDate}
                            getOptionLabel={(option) => t(option.label)}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t("arenaArchive.uploadDate")}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <Autocomplete
                            options={durationOptions}
                            value={
                              durationOptions.find(
                                (opt) => opt.value === duration
                              ) || null
                            }
                            onChange={onChangeDuration}
                            getOptionLabel={(option) => t(option.label)}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t("arenaArchive.duration")}
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {displayTabs && <br />}

        <ArenaContents
          arena={arena}
          view="GRID"
          type="VIDEO"
          sort={sort}
          limit={LIMIT}
          search={searchQuery || undefined}
          tagIds={resultTagIds}
          rightholderIds={rightholderIds}
          itemsPerRow={itemsPerRow}
          previewMode={previewMode}
          uploadDate={uploadDate}
          duration={duration}
          noMore
          loadMore
          inView
        />
      </Box>
    </>
  );
}

ArenaArchive.propTypes = {
  title: PropTypes.string,
  itemsPerRow: PropTypes.number,
  allVideosText: PropTypes.string,
  search: PropTypes.bool,
  filterTagsBySelectedTags: PropTypes.bool,
  advancedFilters: PropTypes.bool,
  filterTagTypes: PropTypes.arrayOf(
    PropTypes.shape({
      tagTypeId: PropTypes.string,
      sort: PropTypes.string,
    })
  ),
  tagTypeId: PropTypes.string,
  tagsSort: PropTypes.string,
  arena: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  rightholderIds: PropTypes.arrayOf(PropTypes.string),
  tagIds: PropTypes.arrayOf(PropTypes.string),
  previewMode: PropTypes.oneOf(["HORIZONTAL", "VERTICAL"]),
  sort: PropTypes.string,
};
